import { useEffect, useRef, useState } from "react";

type ProgressCircleProps = {
    initial?: number;
    value: number;
    total?: number;
    // direction?: "CW" | "CCW";
    duration?: number;
    progressColor?: string; // hex color 
    remainingColor?: string; // hex color 
    textMode?: "percent" | "fraction";
    smallText?: string; // e.g., "Loading", "Processing"
    width?: string; // e.g., "100%", "80%", etc.
    // strokeWidth?: number; // relative to 100 viewBox units
    fontSize?: number; // in rem units
};

export default function ProgressCircle({
    initial = 0,
    value,
    total = 100,
    // direction = "CW",
    duration = 400,
    progressColor = "#30812c", // CA state template --brand-success
    // progressColor = "#198754", // default Bootstrap success green
    remainingColor = "#dee2e6", // default Bootstrap $gray-300
    textMode = "percent",
    smallText,
    width = "100%",
    // strokeWidth = 10,
    fontSize = 0.875 // em
}: ProgressCircleProps) {
    const strokeWidth = 5; // relative to 100 viewBox units
    const radius = 50 - strokeWidth / 2; // SVG units
    const circumference = 2 * Math.PI * radius;

    const [progress, setProgress] = useState(initial);
    const requestRef = useRef<number>(0);

    useEffect(() => {
        if (duration === 0 || initial === value || total === 0) {
            setProgress(value);
            return;
        }

        const startTime = performance.now();

        const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progressValue = Math.min(1, elapsed / duration);
            const current = initial + (value - initial) * progressValue;
            setProgress(current);

            if (progressValue < 1) {
                requestRef.current = requestAnimationFrame(animate);
            } else {
                cancelAnimationFrame(requestRef.current);
            }
        };

        requestRef.current = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(requestRef.current);
    }, [initial, value, duration]);

    const percent = total === 0 ? total : Math.max(0, Math.min(progress / total, 1));
    const strokeDashoffset = circumference * (1 - percent);
    // const rotation = direction === "CCW" ? 180 : 0;

    const displayText =
        textMode === "fraction"
            ? `${value} / ${total}`
            : `${total === 0 ? total : Math.round((value / total) * 100)}%`;

    return (
        <div style={{ position: "relative", width, aspectRatio: "1 / 1" }}>
            <svg
                viewBox="0 0 100 100"
                style={{ width: "100%", height: "100%", display: "block" }}
            >
                {/* <g transform={`rotate(${rotation}, 50, 50)`}> */}
                <g transform={`rotate(-90 50 50)`}>
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="transparent"
                        stroke={remainingColor}
                        strokeWidth={strokeWidth}
                    />
                    <circle
                        cx="50"
                        cy="50"
                        r={radius}
                        fill="transparent"
                        stroke={progressColor}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={strokeDashoffset}
                        strokeLinecap="round"
                        style={{ transition: duration === 0 ? "none" : undefined }}
                    />
                </g>
            </svg>
            <div
                className="lh-1 text-center"
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-45%, -55%)",
                    fontSize: `${fontSize}rem`,
                    // fontFamily: "sans-serif",
                    // color: "#333",
                    pointerEvents: "none",
                    userSelect: "none"
                }}
            >
                <div>{displayText}</div>
                {smallText && <div className="me-1" style={{ fontSize: '0.5em' }}>{smallText}</div>}
            </div>
        </div>
    );
};
